import { useCallback, useMemo } from 'react';

import type { fnGetFilteredProductLinks } from '@atlassian/global-shortcuts';
import { RequestStatus, type ProductLink } from '@atlassian/navigation-bff-data-provider';

import type {
	InvitedAppShortcutAllowedTargetAppKey,
	ShortcutItemsConfig,
} from '../../../common/types';
import {
	getAppKeyByApsKey,
	isAllowedApsKey,
} from '../../../common/utils/invited-app-shortcuts-utils';

type ProductLinksByApsKey = Record<string, ProductLink[]>;
type ProductLinksByProduct = Partial<Record<InvitedAppShortcutAllowedTargetAppKey, ProductLink[]>>;

interface UseInvitedAppProductLinksArgs {
	getCachedProductLinks: () => ProductLinksByProduct;
	shortcutItemsConfig: ShortcutItemsConfig;
	updateHydratedProductLinks: (productLinks: ProductLinksByProduct) => void;
}

interface UseInvitedAppProductLinksResult {
	handleUpdateInvitedAppProductLinks: (getFilteredProductLinks: fnGetFilteredProductLinks) => void;
	invitedAppProductLinks: Record<string, ProductLink[]>;
}

export const useInvitedAppProductLinks = ({
	shortcutItemsConfig,
	getCachedProductLinks,
	updateHydratedProductLinks,
}: UseInvitedAppProductLinksArgs): UseInvitedAppProductLinksResult => {
	const invitedAppProductLinks = useMemo<Record<string, ProductLink[]>>(
		() => resolveCachedInvitedAppProductLinks(shortcutItemsConfig, getCachedProductLinks),
		[shortcutItemsConfig, getCachedProductLinks],
	);

	/**
	 * Update invited app product links when getFilteredProductLinks changes
	 */
	const handleUpdateInvitedAppProductLinks = useCallback(
		(getFilteredProductLinks: fnGetFilteredProductLinks): void => {
			if (typeof getFilteredProductLinks === 'function') {
				const hydratedLinks = resolveInvitedAppProductLinks(
					getFilteredProductLinks,
					shortcutItemsConfig,
				);
				if (Object.keys(hydratedLinks).length > 0) {
					updateHydratedProductLinks(toProductLinksByProduct(hydratedLinks));
				}
			}
		},
		[shortcutItemsConfig, updateHydratedProductLinks],
	);

	return {
		invitedAppProductLinks,
		handleUpdateInvitedAppProductLinks,
	};
};

const resolveInvitedAppProductLinks = (
	getFilteredProductLinks: fnGetFilteredProductLinks,
	shortcutItemsConfig: ShortcutItemsConfig,
) => {
	// No need to resolve links if there are no shortcut items config
	if (Object.keys(shortcutItemsConfig).length === 0) {
		return {};
	}

	let loaded = false;
	const resolved = Object.entries(shortcutItemsConfig).reduce<ProductLinksByApsKey>(
		(acc, [, config]) => {
			if (config.type === 'link' && config.apsKey) {
				const { links, status } = getFilteredProductLinks(config.apsKey);
				// the status is same across all links, so we can use it to determine if the links are loaded
				if (status === RequestStatus.COMPLETE) {
					loaded = true;
				}
				acc[config.apsKey] = links;
			}
			return acc;
		},
		{},
	);
	return loaded ? resolved : {};
};

const resolveCachedInvitedAppProductLinks = (
	shortcutItemsConfig: ShortcutItemsConfig,
	getCachedProductLinks: () => ProductLinksByProduct,
): ProductLinksByApsKey => {
	const cachedProductLinks = getCachedProductLinks();
	if (!cachedProductLinks) {
		return {};
	}

	return Object.values(shortcutItemsConfig).reduce<ProductLinksByApsKey>((acc, config) => {
		if (config.type === 'link' && config.apsKey && isAllowedApsKey(config.apsKey)) {
			const product = getAppKeyByApsKey(config.apsKey);
			const links = cachedProductLinks[product];
			if (links) {
				acc[config.apsKey] = links;
			}
		}
		return acc;
	}, {});
};

const toProductLinksByProduct = (
	productLinksByApsKey: ProductLinksByApsKey,
): ProductLinksByProduct =>
	Object.entries(productLinksByApsKey).reduce<ProductLinksByProduct>((acc, [apsKey, links]) => {
		if (isAllowedApsKey(apsKey)) {
			const product = getAppKeyByApsKey(apsKey);
			acc[product] = links;
		}
		return acc;
	}, {});
